import { create } from "zustand";

/** Resolved appearance applied to the DOM / consumers. */
export type Theme = "dark" | "light";

/** User choice; `system` follows the browser/OS via prefers-color-scheme. */
export type ThemePreference = Theme | "system";

const STORAGE_KEY = "theme";

function systemTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(preference: ThemePreference): Theme {
  return preference === "system" ? systemTheme() : preference;
}

function readPreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark" || saved === "system") return saved;
  } catch {
    /* ignore */
  }
  return "system";
}

function persistPreference(preference: ThemePreference) {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    /* ignore */
  }
}

function applyResolved(theme: Theme) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.classList.toggle("dark", theme === "dark");
}

type ThemeState = {
  preference: ThemePreference;
  /** Resolved light/dark for UI (Monaco, charts, CSS). */
  theme: Theme;
  setPreference: (preference: ThemePreference) => void;
  /** Cycles Light → Dark → System. */
  cycle: () => void;
  /** Flips resolved theme to an explicit light/dark. */
  toggle: () => void;
  setTheme: (theme: Theme) => void;
};

let mediaCleanup: (() => void) | null = null;

function bindSystemListener(followSystem: boolean, onSystemChange: () => void) {
  mediaCleanup?.();
  mediaCleanup = null;
  if (!followSystem || typeof window === "undefined") return;

  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => onSystemChange();
  mq.addEventListener("change", handler);
  mediaCleanup = () => mq.removeEventListener("change", handler);
}

function initialPreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  return readPreference();
}

export const useThemeStore = create<ThemeState>()((set, get) => {
  const preference = initialPreference();
  const theme = resolveTheme(preference);

  const syncFromSystem = () => {
    const state = get();
    if (!state || state.preference !== "system") return;
    const next = resolveTheme("system");
    applyResolved(next);
    set({ theme: next });
  };

  bindSystemListener(preference === "system", syncFromSystem);

  return {
    preference,
    theme,
    setPreference: (next) => {
      persistPreference(next);
      const resolved = resolveTheme(next);
      applyResolved(resolved);
      set({ preference: next, theme: resolved });
      bindSystemListener(next === "system", syncFromSystem);
    },
    cycle: () => {
      const order: ThemePreference[] = ["light", "dark", "system"];
      const i = order.indexOf(get().preference);
      const next = order[(i + 1) % order.length]!;
      get().setPreference(next);
    },
    toggle: () => {
      const next: Theme = get().theme === "dark" ? "light" : "dark";
      get().setPreference(next);
    },
    setTheme: (theme) => {
      get().setPreference(theme);
    },
  };
});

/** Apply persisted preference once on load (before React paint when called from main). */
export function initTheme() {
  const { preference, theme } = useThemeStore.getState();
  persistPreference(preference);
  applyResolved(theme);
}

export function useTheme() {
  const theme = useThemeStore((s) => s.theme);
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);
  const setTheme = useThemeStore((s) => s.setTheme);
  const toggle = useThemeStore((s) => s.toggle);
  const cycle = useThemeStore((s) => s.cycle);
  return { theme, preference, setPreference, setTheme, toggle, cycle };
}
